import Vue from 'vue';
import router from "@/router";

import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {
  EventBus
} from "@/services/event-bus.js";

import * as LocalStorage from '@/services/localstorage';

axios.options.xhr = {
  withCredentials: true
};

// if the config file has a typo, you'll get weird errors here :(
const ENDPOINT = `${process.env.NODE_ENV}` === 'development' 
  ? `http://${location.host.replace(/:\d{4}$/,':3000')}` 
  : `${process.env.API_ENDPOINT}`;
const API_KEY = `${process.env.API_KEY}`;
const APIPREFIX = '/api/1.0';

// auth interceptor
createAuthRefreshInterceptor(
  axios, 
  failedRequest => refreshToken()
    .then((refreshResult) => {
      const user = LocalStorage.getUser();
      user.tokenExpiry = refreshResult.data.tokenExpiry;
      LocalStorage.storeUser(user);
    }),
  {
    pauseInstanceWhileRefreshing: true,
    interceptNetworkError: true
  }
);



// Intercept request
axios.interceptors.request.use(async (request) => {
  if (!(/(password-reset)|(authenticate)/g.test(request.url))) {
    try {
      Vue.$cookies.get('xsrf')
    } catch (e) {
      console.log(e);
    }
    request.headers.Authorization = `Bearer ${Vue.$cookies.get('xsrf')}`;
  } else {
    request.headers['x-api-key'] = API_KEY;
  }
  request.withCredentials = true;
  return request;
});

const handleError = (error) => {
  if (!error.response || !error.response.status) {  
    return error;
  }
  switch (error.response.status) {
  case 401:
    router.push('/login');
    break;
  case 404:
    router.push("/not-found");
    break;
    // used by user entry form
  case 409:
  case 422:
    error.data = error;
    error.data.iserror = true;
    return error;
    break;
    // Gateway timeout
  case 504:
    EventBus.$emit("triggerAlert", {
      type: "danger",
      message: "The server encounted an error and couldn't complete your request."
    });
    EventBus.$emit("hideLoader");
    break;
  default:
    // unknown error, so I'm going to 
    // drop the user back to login screen - safest option
    router.push('/login');
    break;
  }
};

/** --------------------
 *
 *    Helper functions
 *
 * --------------------- */

const getRequest = async (url) => {
  return axios
    .get(`${ENDPOINT}${url}`)
    .catch(error => handleError(error))
    .then(res => {
      return res.data;
    });
};

const postRequest = async (url, body) => {
  return axios.post(`${ENDPOINT}${url}`, body).catch(error => handleError(error));
};

const putRequest = async (url, body) => {
  return axios.put(`${ENDPOINT}${url}`, body).catch(error => handleError(error));
};

const deleteRequest = async (url) => {
  return axios.delete(`${ENDPOINT}${url}`).catch(error => handleError(error));
};

/** -----------------
 *
 *   Login
 *
 * ----------------- */

const loginUser = async (username, password, rememberMe) => {
  const body = {
    username,
    password,
    rememberMe
  }
  return axios.post(`${ENDPOINT}/authenticate`, body).then(response => {
    response.body = response.data;
    return response;
  });
};

const resetPassword = async (username) => {
  const body = {
    emailAddress: username
  }
  return axios.post(`${ENDPOINT}/password-reset`, body);
};

const resetPasswordRedemption = async (username, password, token) => {
  const body = {
    emailAddress: username,
    newPassword: password
  }
  return axios.post(`${ENDPOINT}/password-reset/${token}`, body);
};

const logout = async () => {
  return deleteRequest('/tokens/invalidate');
}

/** -----------------
 *
 *   Jobs
 *
 * ----------------- */

const searchJobs = async (params) => {
  return postRequest(`${APIPREFIX}/jobs/search`, params).then(result => {
    return result.data;
  });
};

const searchSamples = async (params) => {
  return postRequest(`${APIPREFIX}/jobs/${params.searchFields.jobId || 'all'}/samples/search`, params).then(result => {
    return result.data;
  });
};

const getJob = async (id) => {
  return getRequest(`${APIPREFIX}/jobs/${id}`);
};

const getSample = async (jobId, sampleId) => {
  return getRequest(`${APIPREFIX}/jobs/${jobId}/samples/${sampleId}`);
};

const getDocumentUrl = async (id, docName) => {
  return getRequest(`${APIPREFIX}/jobs/${id}/documents/${encodeURI(docName)}`);
};

// analytes = results
const searchJobResults = async (id, params) => {
  return postRequest(`${APIPREFIX}/jobs/${id}/samples/all/analytes/search`, params).then(result => {
    return result.data;
  });
};

/** -----------------
 *
 *   Users
 *
 * ----------------- */

const getUser = async (id) => {
  return getRequest(`/users/${id}`);
};

const searchUsers = (params) => {
  return postRequest('/users/search', params).then(result => {
    return result.data;
  });
}

const addUser = (params) => {
  return postRequest('/users', params).then(result => {
    return result.data;
  });
}

const updateUser = (id, params) => {
  return postRequest(`/users/${id}`, params).then(result => {
    return result.data;
  });
};

const deleteUser = (id) => {
  return deleteRequest(`/users/${id}`).then(result => {
    return result.data;
  });
};

const updateApiKey = (id) => {
  return postRequest(`/users/${id}/api-key`, null).then(result => {
    return result.data;
  });
};

const refreshToken = () => {
  return postRequest('/tokens/refresh', null);
};

/** -----------------
 *
 *   Clients
 *
 * ----------------- */

const getAllClients = async () => {
  return getRequest('/clients?pageSize=10000').then(data => {
    return data.results;
  });
}

export {
  loginUser,
  resetPassword,
  getUser,
  searchUsers,
  addUser,
  updateUser,
  updateApiKey,
  deleteUser,
  getAllClients,
  searchJobs,
  getJob,
  getSample,
  getDocumentUrl,
  searchJobResults,
  searchSamples,
  logout,
  resetPasswordRedemption
};


// WEBPACK FOOTER //
// ./src/services/api.js